import React, { useEffect, useState } from "react";
import DropdownsForAttendanceStatisticsByDate from "./components/DropdownsForAttendanceStatisticsByDate";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import { useLocation } from "react-router-dom";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import { generateRegistrationRows } from "../../../util/dealingArrObj";
import AttendanceStatisticsTableByDate from "./components/AttendanceStatisticsTableByDate";
import useAttendance from "../../../hooks/useAttendance";
import useSchoolYearPeriod from "../../../hooks/useSchoolYearPeriod";
import { useUIContext } from "../../../context/UIContext";
import LoadingInside from "../../../unicomponents/LoadingInside";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import useTermPeriod from "../../../hooks/useTermPeriod";
import { convertToYearMonthDayFormat } from "../../../util/formatdate";
import useFlexibleData from "../../../hooks/useFlexibleData";

/*
1. 처음 로드 할 때 학년도 기준으로 start_date와 end_date를 설정해준다.
2. 학년도 변경시 해당 학년도의 start_date와 end_date를 설정해준다.
3. 학기 변경시 해당 학기의 start_date와 end_date를 설정해준다.
4. start_date와 end_date는 학년도의 첫 날과 마지막 날 사이에서만 가능하다.
5. 날짜 변경시 학기는 reset 된다.
6. 학년도의 시작날짜와 끝 날짜는 항상 표시해 둔다.
*/

export default function AttendanceStatisticsByDate() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  // 배열의 두 번째 요소를 schoolId로 사용합니다.
  const schoolId = paths[1];
  //아래 조건으로 데이터를 불러오도록 합니다.
  const conditions = {
    schoolid: schoolId,
  };

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);

  const {
    registrationInfoQuery: { isLoading, data: registrationInfoData },
  } = useRegistrationInfo(conditions);

  const { attendanceQuery } = useAttendance(conditions);

  const {
    schoolYearPeriodQuery: { data: schoolYearPeriodData },
  } = useSchoolYearPeriod(conditions);

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod(conditions);

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({ ...conditions, main_category: "schoolSetting" });

  const additionalAttendance =
    flexibleData &&
    flexibleData.length > 0 &&
    flexibleData[0].data_value?.additionalAttendance;

  const { setAlertModalState } = useUIContext();

  const { hasPermission } = useAuthContext();

  //필터를 위한 모음
  const [organSettingInfo, setOrganSettingInfo] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    selectedStudent: "",
    organPathForSort: "/",
  });

  const [rows, setRows] = useState([]);
  const [attendanceRecord, setAttendanceRecord] = useState([]);
  const [termId, setTermId] = useState("");
  const [attendanceMode, setAttendanceMode] = useState("default");
  const [selectedPeriod, setSelectedPeriod] = useState({
    start_date: "",
    end_date: "",
  });
  const [selectedSchoolYearPeriod, setSelectedSchoolYearPeriod] = useState({
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    if (!additionalAttendance && attendanceMode !== "default") {
      setAttendanceMode("default");
    }
  }, [additionalAttendance, attendanceMode]);

  useEffect(() => {
    let initialSchoolYear = null;
    let initialSchoolYearId = null;

    if (organSettingInfo.schoolYear === null) {
      if (registrationInfoData && settingData && settingData.length > 0) {
        const initialData = settingData.sort(
          (a, b) => b.school_year - a.school_year
        )[0];

        initialSchoolYear = initialData.school_year;
        initialSchoolYearId = initialData.id;
        setOrganSettingInfo((organSettingInfo) => ({
          ...organSettingInfo,
          schoolYear: initialSchoolYear,
          schoolYearId: initialSchoolYearId,
        }));
        //rows 데이터처리
        if (initialSchoolYear) {
          const sortedRows = generateRegistrationRows(
            registrationInfoData,
            settingData,
            initialSchoolYear,
            organSettingInfo.organPathForSort
          );

          setRows(sortedRows);
        }
      }
    } else {
      //rows 데이터처리
      if (registrationInfoData && settingData) {
        // console.log("settingData", settingData);
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          settingData,
          organSettingInfo.schoolYear,
          organSettingInfo.organPathForSort
        );

        setRows(sortedRows);
        // console.log("sortedRows2", sortedRows);
      }
    }
  }, [
    organSettingInfo.organPathForSort,
    organSettingInfo.schoolYear,
    organSettingInfo.schoolYearId,
    registrationInfoData,
    settingData,
  ]);

  useEffect(() => {
    // 데이터가 존재하고, 학년 ID가 정의되어 있으며, attendanceQuery도 사용 가능한 경우에만 실행
    if (
      selectedSchoolYearPeriod.start_date &&
      selectedSchoolYearPeriod.end_date &&
      selectedPeriod.start_date &&
      selectedPeriod.end_date &&
      attendanceQuery
    ) {
      if (
        selectedPeriod.start_date < selectedSchoolYearPeriod.start_date ||
        selectedPeriod.end_date > selectedSchoolYearPeriod.end_date
      ) {
        setAlertModalState({
          visible: true,
          message: t("teacher.attendanceStatistics.invalidDate"),
        });
      }
      if (
        /^\d{4}-\d{2}-\d{2}$/.test(selectedPeriod.start_date) &&
        /^\d{4}-\d{2}-\d{2}$/.test(selectedPeriod.end_date)
      ) {
        const queryConditions = {
          schoolid: schoolId,
          startdate: selectedPeriod.start_date,
          enddate: selectedPeriod.end_date,
        };
        attendanceQuery.mutate(queryConditions, {
          onSuccess: (data) => {
            setAttendanceRecord(data);
          },
          onError: (error) => {
            console.error(error);
            alert(error.message);
          },
        });
      } else {
        setAlertModalState({
          visible: true,
          message: t("teacher.attendanceStatistics.invalidSchoolYearPeriod"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod.start_date, selectedPeriod.end_date, schoolId, t]); // refetchData 함수는 useCallback에 의해 메모이제이션되어, organSettingInfo.schoolYearId가 변경될 때만 재생성됩니다.

  // termId가 변경될 때마다 termName 설정
  useEffect(() => {
    if (termId && termPeriodData) {
      const selectedTerm = termPeriodData.find(
        (term) => term.id.toString() === termId.toString()
      );
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        termName: selectedTerm?.term_name || "",
      }));

      setSelectedPeriod((prev) => ({
        ...prev,
        start_date: selectedTerm?.start_date,
        end_date: selectedTerm?.end_date,
      }));
    } else {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        termName: "",
      }));
    }
  }, [termId, termPeriodData]);

  useEffect(() => {
    if (schoolYearPeriodData) {
      const thisSchoolYearPeriod =
        getThisSchoolYearPeriod(schoolYearPeriodData);

      // console.log("thisSchoolYearPeriod", thisSchoolYearPeriod);
      setSelectedPeriod((prev) => ({
        ...prev,
        start_date: thisSchoolYearPeriod?.start_date,
        end_date: thisSchoolYearPeriod?.end_date,
      }));

      setSelectedSchoolYearPeriod((prev) => ({
        ...prev,
        start_date: thisSchoolYearPeriod?.start_date,
        end_date: thisSchoolYearPeriod?.end_date,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYearPeriodData, organSettingInfo.schoolYearId]);

  const getThisSchoolYearPeriod = (schoolYearPeriodData) => {
    let filteredPeriod = [];
    let hierarchyOnePeriod = [];
    let hierarchyTwoPeriod = [];

    filteredPeriod = schoolYearPeriodData.filter(
      (r) =>
        r.school_year_id.toString() === organSettingInfo.schoolYearId.toString()
    );

    hierarchyOnePeriod = filteredPeriod.filter(
      (r) =>
        r.applied_hierarchy_one !== null &&
        r.applied_hierarchy_one === Number(organSettingInfo.hierarchyOneId)
    );

    if (hierarchyOnePeriod.length > 0) {
      filteredPeriod = hierarchyOnePeriod;
    }

    hierarchyTwoPeriod = filteredPeriod.filter(
      (r) =>
        r.applied_hierarchy_two !== null &&
        r.applied_hierarchy_two === Number(organSettingInfo.hierarchyTwoId)
    );

    if (hierarchyTwoPeriod.length > 0) {
      filteredPeriod = hierarchyTwoPeriod;
    }

    if (filteredPeriod.length > 0) {
      filteredPeriod = filteredPeriod.sort((a, b) => {
        // hierarchy_two_name이 "전체"일 경우 우선 정렬
        if (
          a.hierarchy_two_name === "전체" &&
          b.hierarchy_two_name !== "전체"
        ) {
          return -1; // a가 b보다 앞에 오도록
        } else if (
          a.hierarchy_two_name !== "전체" &&
          b.hierarchy_two_name === "전체"
        ) {
          return 1; // b가 a보다 앞에 오도록
        }

        // hierarchy_one_name이 "전체"일 경우 우선 정렬
        if (
          a.hierarchy_one_name === "전체" &&
          b.hierarchy_one_name !== "전체"
        ) {
          return -1;
        } else if (
          a.hierarchy_one_name !== "전체" &&
          b.hierarchy_one_name === "전체"
        ) {
          return 1;
        }

        return 0; // 그 외의 경우 순서 유지
      });
    }

    const thisYearDates = filteredPeriod.length > 0 ? filteredPeriod[0] : null;
    return thisYearDates;
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    const convertedDate = convertToYearMonthDayFormat(new Date(value));
    setSelectedPeriod((prev) => ({
      ...prev,
      [name]: convertedDate,
    }));
    // 날짜 변경시 학기 reset (요구사항 5번)
    setTermId("");
  };

  const handleAttendanceModeChange = (mode) => {
    if (attendanceMode === mode) return;

    setAttendanceMode(mode);
  };

  const statisticsTitle =
    attendanceMode === "additional"
      ? `${additionalAttendance?.title || "추가 출석"} 통계`
      : t("teacher.attendanceStatistics.attendanceStatisticsByDate");

  //아래 dropdown에 세트로 들어가야 할 애들이다.
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;

    const dataName = e.target.selectedOptions[0].getAttribute("data-name");
    const organPath = dataName + "/" + value;

    let lastIndex = organSettingInfo.organPathForSort.lastIndexOf("/");
    const previousPath = organSettingInfo.organPathForSort.substring(
      0,
      lastIndex
    );
    // 100%는 아니지만 적당히 작동하니까...
    // console.log("previousPath", previousPath);
    // console.log("organPath", organPath);

    if (!organPath.startsWith("nu") && name !== "termDate") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        organPathForSort: organPath,
      }));
    } else if (name !== "termDate") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        organPathForSort: previousPath,
      }));
    }

    if (name === "0") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "0",
      }));
    }

    if (name === "1") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      }));
    }

    if (name === "2") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
      }));
    }

    if (name === "3") {
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        hierarchyThreeId: value,
      }));
    }
    if (name === "termDate") {
      setTermId(value);
      setOrganSettingInfo((organSettingInfo) => ({
        ...organSettingInfo,
        termId: value,
      }));
    }
  };

  // Function to handle printing the statistics table
  const handlePrint = (schoolYear, selectedPeriod) => {
    const printContent = document.getElementById("printArea").innerHTML;
    const printWindow = window.open("", "_blank", "height=600,width=800");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <title>${schoolYear}년 ${selectedPeriod?.start_date} ~ ${selectedPeriod?.end_date} ${statisticsTitle}</title>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.1/dist/css/bootstrap.min.css" rel="stylesheet" integrity="sha384-QWTKZyjpPEjISv5WaRU9OFeRpok6YctnYmDr5pNlyT2bRjXh0JMhjY6hW+ALEwIH" crossorigin="anonymous">
        <style>
          body { font-family: Arial, sans-serif; padding-right: 20px;}
          @page { size: landscape; margin: 10mm;} /* 가로 방향으로 인쇄 설정 */
          .page-break { page-break-after: always; } /* 페이지 브레이크 스타일 */

          .monthTitle { display: none; } /* 기본적으로 monthTitle 숨김 */
          @media print {
            .monthTitle { display: block; } /* 인쇄 시에만 monthTitle 표시 */
          }

  .secondTitle {
    margin-bottom: 10px;
  }

          /* 인쇄할 테이블의 스타일 */
          .tableStyle th {
            padding: 0px;
            text-align: center;
            background-color: #f0f0f0;
          }
          
          .tableStyle td {
            padding: 0px;
            text-align: center;
          }
          
          .tableStyle {
            font-size: 12px;
            width: 100%;
            border-collapse: collapse;
          }
  
          th, td { border: 1px solid black; } /* 모든 테이블 셀에 테두리 추가 */
        </style>
      </head>
      <body>
        ${printContent}
        <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.1/dist/js/bootstrap.bundle.min.js" integrity="sha384-YvpcrYf0tY3lHB60NNkmXc5s9fDVZLESaAA55NDzOxhy9GkcIdslK1eN7N6jIeHz" crossorigin="anonymous"></script>
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.onload = function () {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  // Function to handle exporting the statistics to Excel
  // const handleExport = () => {
  //   // exportToExcel('attendance-table');
  // };

  if (isLoading) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access")) {
    return (
      <SimpleAlertMessageBlock
        message={t("common.messages.noAccessPermission")}
      />
    );
  }

  // Excel 다운로드 함수 구현
  const handleExport = () => {
    const tableEl = document.querySelector(".tableStyle");
    if (!tableEl) return;

    // 새 워크북 생성
    const wb = XLSX.utils.book_new();

    // 모든 테이블을 순회하며 데이터 추출
    const tables = document.querySelectorAll("#printArea .tableStyle");

    tables.forEach((table, index) => {
      // 테이블을 워크시트로 변환
      const ws = XLSX.utils.table_to_sheet(table);

      // 워크시트 스타일 조정
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
          if (!cell) continue;

          // 셀 스타일 설정
          cell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
          };
        }
      }

      // 워크북에 워크시트 추가
      let sheetName = `attendance${index}`; // 기본 시트 이름 설정
      const secondTitleElements = document.querySelectorAll(".secondTitle");

      if (secondTitleElements.length > index) {
        const classInfo = secondTitleElements[index].textContent.trim();
        if (classInfo) {
          // 공백을 언더스코어로 대체하고 31자로 제한
          sheetName = classInfo.replace(/\s+/g, "_").substring(0, 31);
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // 파일 다운로드
    const fileName = `${organSettingInfo.schoolYear}-${selectedPeriod?.start_date}-${selectedPeriod?.end_date}-${statisticsTitle}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <>
      <div className="ms-3 basicWidth">
        <div className="container-fluid">
          <div className="row mb-4 mt-2">
            <div className="col-3">
              <div className="d-flex">
                <h3
                  className={`fw-bold mt-3 ${
                    attendanceMode === "additional" ? "text-info" : ""
                  }`}
                >
                  {statisticsTitle}
                </h3>
              </div>
            </div>
            <div className="col-9 d-flex gap-2 align-items-center">
              {additionalAttendance && (
                <div className="btn-group flex-shrink-0" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      attendanceMode === "default"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => handleAttendanceModeChange("default")}
                  >
                    {t(
                      "teacher.attendanceStatistics.attendanceStatisticsByDate"
                    )}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      attendanceMode === "additional"
                        ? "btn-info"
                        : "btn-outline-info"
                    }`}
                    onClick={() => handleAttendanceModeChange("additional")}
                  >
                    {additionalAttendance.title || "추가 출석"}
                  </button>
                </div>
              )}
              <DropdownsForAttendanceStatisticsByDate
                settingData={settingData}
                handleDropdownChange={handleDropdownChange}
                organSettingInfo={organSettingInfo}
                termPeriodData={termPeriodData}
                termId={termId}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-7 d-flex align-items-center">
              <div
                className="ms-2 fw-bold"
                style={{ width: "80px", minWidth: "80px" }}
              >
                {t("teacher.attendanceStatistics.selectPeriod") || "기간 선택"}
              </div>
              <input
                className="form-control"
                type="date"
                name="start_date"
                value={selectedPeriod?.start_date || ""}
                min={selectedSchoolYearPeriod?.start_date}
                max={selectedSchoolYearPeriod?.end_date}
                onChange={(e) => handleDateChange(e)}
              />
              <span className="mx-2 fw-bold">~</span>
              <input
                className="form-control"
                type="date"
                name="end_date"
                value={selectedPeriod?.end_date || ""}
                min={selectedSchoolYearPeriod?.start_date}
                max={selectedSchoolYearPeriod?.end_date}
                onChange={(e) => handleDateChange(e)}
              />
              {/* <ChangeMonth preNextMonth={preNextMonth} /> */}
            </div>
            <div className="col-md-5 col-12 d-flex justify-content-end">
              <button
                className="btn btn-info me-3"
                onClick={() =>
                  handlePrint(organSettingInfo.schoolYear, selectedPeriod)
                }
              >
                {t("common.labels.print")}
              </button>
              <button className="btn btn-success" onClick={handleExport}>
                To Excel
              </button>
            </div>
          </div>
          <div id="printArea">
            {organSettingInfo.hierarchyOneId &&
              organSettingInfo.hierarchyOneId !== "0" &&
              // key 3의 유니크한 값들을 추출
              [...new Set(rows.map((row) => row.organization_path))].map(
                (uniqueKey) => {
                  // 각 유니크한 key 값에 대해 rows를 필터링
                  // console.log("rows", rows);
                  // console.log("uniqueKey", uniqueKey);

                  const filteredRows = rows.filter(
                    (row) => row.organization_path === uniqueKey
                  );
                  // console.log("filteredRows", filteredRows);
                  return (
                    <div key={uniqueKey} className="page">
                      <h4 className="monthTitle d-none">
                        {t("teacher.attendanceStatistics.printTitle")}
                      </h4>
                      <div className="secondTitle">{`${filteredRows[0][1]} ${t(
                        "common.labels.schoolStage"
                      )} ${filteredRows[0][2]} ${t(
                        "common.labels.studentYear"
                      )} ${filteredRows[0][3]} ${t(
                        "common.labels.class"
                      )}`}</div>
                      <AttendanceStatisticsTableByDate
                        rows={filteredRows}
                        attendanceRecord={attendanceRecord}
                        organSettingInfo={organSettingInfo}
                        termPeriodData={termPeriodData}
                        selectedPeriod={selectedPeriod}
                        conditions={conditions}
                        attendanceMode={attendanceMode}
                        additionalAttendance={additionalAttendance}
                      />
                      <div className="page-break"></div> {/* 페이지 분리 */}
                    </div>
                  );
                }
              )}
          </div>
        </div>
      </div>
    </>
  );
}
